// // ============================================================
// // FILE: src/app/shared/seo.service.ts
// // USAGE: Inject into any component that needs SEO management
// // ============================================================

// import { Injectable, Inject } from '@angular/core';
// import { Title, Meta } from '@angular/platform-browser';
// import { DOCUMENT } from '@angular/common';

// export interface SeoConfig {
//   title: string;
//   description: string;
//   keywords?: string;
//   canonicalUrl: string;
//   ogTitle?: string;
//   ogDescription?: string;
//   ogImage?: string;
//   ogType?: string;
//   twitterTitle?: string;
//   twitterDescription?: string;
//   twitterImage?: string;
//   robots?: string;
//   schema?: object | object[];
// }

// @Injectable({ providedIn: 'root' })
// export class SeoService {
//   constructor(
//     private titleService: Title,
//     private metaService: Meta,
//     @Inject(DOCUMENT) private document: Document
//   ) {}

//   /**
//    * Apply complete SEO config to the current page.
//    * Call this in ngOnInit() of each page component.
//    */
//   applySeo(config: SeoConfig): void {
//     // ── Title ──────────────────────────────────────────────
//     this.titleService.setTitle(config.title);

//     // ── Standard meta ──────────────────────────────────────
//     this.updateTag('name', 'description', config.description);
//     if (config.keywords) {
//       this.updateTag('name', 'keywords', config.keywords);
//     }
//     this.updateTag('name', 'robots', config.robots || 'index, follow');

//     // ── Open Graph ─────────────────────────────────────────
//     this.updateTag('property', 'og:title',       config.ogTitle       || config.title);
//     this.updateTag('property', 'og:description', config.ogDescription || config.description);
//     this.updateTag('property', 'og:url',         config.canonicalUrl);
//     this.updateTag('property', 'og:type',        config.ogType || 'website');
//     this.updateTag('property', 'og:site_name',   'NEXGEN Executive Coaching Academy');
//     if (config.ogImage) {
//       this.updateTag('property', 'og:image', config.ogImage);
//       this.updateTag('property', 'og:image:width',  '1200');
//       this.updateTag('property', 'og:image:height', '630');
//       this.updateTag('property', 'og:image:alt',    config.ogTitle || config.title);
//     }

//     // ── Twitter Card ───────────────────────────────────────
//     this.updateTag('name', 'twitter:card',        'summary_large_image');
//     this.updateTag('name', 'twitter:site',        '@nexgencoaches');
//     this.updateTag('name', 'twitter:title',       config.twitterTitle       || config.title);
//     this.updateTag('name', 'twitter:description', config.twitterDescription || config.description);
//     if (config.twitterImage || config.ogImage) {
//       this.updateTag('name', 'twitter:image', (config.twitterImage || config.ogImage)!);
//     }

//     // ── Canonical ──────────────────────────────────────────
//     this.setCanonical(config.canonicalUrl);

//     // ── JSON-LD Schema ─────────────────────────────────────
//     if (config.schema) {
//       this.injectSchema(config.schema);
//     }
//   }

//   /**
//    * Inject (or replace) JSON-LD schema in <head>.
//    * Accepts a single schema object or an array for @graph usage.
//    */
//   injectSchema(schema: object | object[]): void {
//     const existing = this.document.getElementById('page-schema');
//     if (existing) existing.remove();

//     const script = this.document.createElement('script');
//     script.id   = 'page-schema';
//     script.type = 'application/ld+json';

//     const payload = Array.isArray(schema)
//       ? { '@context': 'https://schema.org', '@graph': schema }
//       : schema;

//     script.textContent = JSON.stringify(payload, null, 0);
//     this.document.head.appendChild(script);
//   }

//   /** Remove all SEO tags injected by this service (call on component destroy). */
//   clearSeo(): void {
//     const schemaEl = this.document.getElementById('page-schema');
//     if (schemaEl) schemaEl.remove();
//   }

//   // ── Private helpers ──────────────────────────────────────

//   private updateTag(attr: 'name' | 'property', key: string, value: string): void {
//     const selector = `${attr}="${key}"`;
//     if (this.metaService.getTag(selector)) {
//       this.metaService.updateTag({ [attr]: key, content: value });
//     } else {
//       this.metaService.addTag({ [attr]: key, content: value });
//     }
//   }

//   private setCanonical(url: string): void {
//     let link: HTMLLinkElement | null =
//       this.document.querySelector('link[rel="canonical"]');
//     if (!link) {
//       link = this.document.createElement('link');
//       link.setAttribute('rel', 'canonical');
//       this.document.head.appendChild(link);
//     }
//     link.setAttribute('href', url);
//   }
// }












// ============================================================
// FILE: src/app/shared/seo.service.ts  (UPDATED — replaces previous version)
//
// FIXES IN THIS VERSION:
// 1. clearSeo() now removes ALL injected meta tags, not just schema
// 2. applySeo() adds hreflang tags (fixes international duplicate content)
// 3. setHreflang() helper added
// 4. removeAllInjectedMeta() helper added
// ============================================================

import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  robots?: string;
  schema?: object | object[];
  hreflang?: string; // defaults to 'en'
}

@Injectable({ providedIn: 'root' })
export class SeoService {

  // Track which meta tags we've injected so we can clean them up
  private injectedMetaNames: string[] = [];
  private injectedMetaProperties: string[] = [];

  constructor(
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Apply complete SEO config to the current page.
   * Call this in ngOnInit() of each page component.
   */
  applySeo(config: SeoConfig): void {
    // ── Title ──────────────────────────────────────────────
    this.titleService.setTitle(config.title);

    // ── Standard meta ──────────────────────────────────────
    this.upsertName('description', config.description);
    if (config.keywords) {
      this.upsertName('keywords', config.keywords);
    }
    this.upsertName('robots', config.robots || 'index, follow');

    // ── Open Graph ─────────────────────────────────────────
    this.upsertProperty('og:title',       config.ogTitle       || config.title);
    this.upsertProperty('og:description', config.ogDescription || config.description);
    this.upsertProperty('og:url',         config.canonicalUrl);
    this.upsertProperty('og:type',        config.ogType || 'website');
    this.upsertProperty('og:site_name',   'NEXGEN Executive Coaching Academy');
    this.upsertProperty('og:locale',      'en_US');

    if (config.ogImage) {
      this.upsertProperty('og:image',        config.ogImage);
      this.upsertProperty('og:image:width',  '1200');
      this.upsertProperty('og:image:height', '630');
      this.upsertProperty('og:image:alt',    config.ogTitle || config.title);
      this.upsertProperty('og:image:type',   'image/jpeg');
    }

    // ── Twitter Card ───────────────────────────────────────
    this.upsertName('twitter:card',        'summary_large_image');
    this.upsertName('twitter:site',        '@nexgencoaches');
    this.upsertName('twitter:title',       config.twitterTitle       || config.title);
    this.upsertName('twitter:description', config.twitterDescription || config.description);
    if (config.twitterImage || config.ogImage) {
      this.upsertName('twitter:image', (config.twitterImage || config.ogImage)!);
    }

    // ── Canonical ──────────────────────────────────────────
    this.setCanonical(config.canonicalUrl);

    // ── Hreflang (FIX: was missing — prevents international duplicate content) ──
    this.setHreflang(config.canonicalUrl, config.hreflang || 'en');

    // ── JSON-LD Schema ─────────────────────────────────────
    if (config.schema) {
      this.injectSchema(config.schema);
    }
  }

  /**
   * Inject (or replace) JSON-LD schema in <head>.
   * Accepts a single schema object or an array for @graph usage.
   */
  injectSchema(schema: object | object[]): void {
    const existing = this.document.getElementById('page-schema');
    if (existing) existing.remove();

    const script = this.document.createElement('script');
    script.id   = 'page-schema';
    script.type = 'application/ld+json';

    const payload = Array.isArray(schema)
      ? { '@context': 'https://schema.org', '@graph': schema }
      : schema;

    script.textContent = JSON.stringify(payload, null, 0);
    this.document.head.appendChild(script);
  }

  /**
   * FIX: clearSeo() now removes ALL injected meta, canonical, hreflang + schema.
   * Call this in ngOnDestroy() of each page component.
   * 
   * Previous version only removed the schema script tag — leaving stale
   * og:title, description, etc. if the next page didn't call applySeo().
   */
  clearSeo(): void {
    // Remove JSON-LD schema
    const schemaEl = this.document.getElementById('page-schema');
    if (schemaEl) schemaEl.remove();

    // Remove all meta[name] tags we injected
    this.injectedMetaNames.forEach(name => {
      this.metaService.removeTag(`name="${name}"`);
    });
    this.injectedMetaNames = [];

    // Remove all meta[property] tags we injected
    this.injectedMetaProperties.forEach(prop => {
      this.metaService.removeTag(`property="${prop}"`);
    });
    this.injectedMetaProperties = [];

    // Remove canonical
    const canonical = this.document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.remove();

    // Remove hreflang tags
    const hreflangLinks = this.document.querySelectorAll('link[rel="alternate"][hreflang]');
    hreflangLinks.forEach(el => el.remove());
  }

  // ── Private helpers ──────────────────────────────────────

  /** Upsert a meta[name] tag and track it for cleanup */
  private upsertName(name: string, content: string): void {
    const selector = `name="${name}"`;
    if (this.metaService.getTag(selector)) {
      this.metaService.updateTag({ name, content });
    } else {
      this.metaService.addTag({ name, content });
    }
    if (!this.injectedMetaNames.includes(name)) {
      this.injectedMetaNames.push(name);
    }
  }

  /** Upsert a meta[property] tag and track it for cleanup */
  private upsertProperty(property: string, content: string): void {
    const selector = `property="${property}"`;
    if (this.metaService.getTag(selector)) {
      this.metaService.updateTag({ property, content });
    } else {
      this.metaService.addTag({ property, content });
    }
    if (!this.injectedMetaProperties.includes(property)) {
      this.injectedMetaProperties.push(property);
    }
  }

  /** Set or update the canonical <link> tag */
  private setCanonical(url: string): void {
    let link: HTMLLinkElement | null =
      this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /**
   * FIX: Add hreflang tags — was missing from previous version.
   * Prevents Google from flagging duplicate content between subdomains.
   * 
   * Adds:
   *   <link rel="alternate" hreflang="en" href="[url]">
   *   <link rel="alternate" hreflang="x-default" href="[url]">
   */
  private setHreflang(url: string, lang: string = 'en'): void {
    // Remove existing hreflang tags to prevent duplicates
    this.document.querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach(el => el.remove());

    // Add language-specific hreflang
    const hreflangLink = this.document.createElement('link');
    hreflangLink.setAttribute('rel', 'alternate');
    hreflangLink.setAttribute('hreflang', lang);
    hreflangLink.setAttribute('href', url);
    this.document.head.appendChild(hreflangLink);

    // Add x-default (tells Google which URL is the default for unmatched languages)
    const defaultLink = this.document.createElement('link');
    defaultLink.setAttribute('rel', 'alternate');
    defaultLink.setAttribute('hreflang', 'x-default');
    defaultLink.setAttribute('href', url);
    this.document.head.appendChild(defaultLink);
  }
}